import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import SearchIcon from "@mui/icons-material/Search";
import { message } from "antd";
import ApiCallService from "../../../Service/ApiCallService";
import ReactTable from "../../../components/common/ReactTable";
import * as XLSX from "xlsx";
import { Doughnut } from "react-chartjs-2";
import CountUpAnimation from "../../../components/common/CountUpAnimation";
import { downloadChapterExcel } from "../../../utils/chapterExcelExport.js";

const LIFECYCLE_STATUS_OPTIONS = ["Prospective", "Vibrant", "Aspirational"];
const normalizeTextValue = (value, fallback = "-") => {
  const text = String(value ?? "").trim();
  return text || fallback;
};

const getPersonName = (value) => {
  if (!value) return "-";
  if (typeof value === "string") return normalizeTextValue(value);
  if (Array.isArray(value)) {
    const names = value.map(getPersonName).filter((name) => name && name !== "-");
    return names.length ? names.join(", ") : "-";
  }

  const directName =
    value.name ||
    value.fullName ||
    value.memberName ||
    value.userName ||
    value.displayName ||
    value.username ||
    value.user?.name ||
    value.user?.fullName ||
    value.userId?.name ||
    value.userId?.fullName;

  if (directName) return normalizeTextValue(directName);

  const joinedName = [value.firstName || value.user?.firstName, value.lastName || value.user?.lastName]
    .filter(Boolean)
    .join(" ");

  return normalizeTextValue(joinedName);
};

const getChapterLocation = (row) =>
  [row.stateName, row.regionName, row.districtName, row.talukaName, row.villageName]
    .map((value) => normalizeTextValue(value, ""))
    .filter((value) => value && value !== "-")
    .join(" - ") || "-";

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const buildChapterExcelHtml = (rows) => {
  const generatedAt = new Date().toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  const bodyRows = rows
    .map(
      (row, index) => `
        <tr>
          <td class="serial">${index + 1}</td>
          <td>${escapeHtml(row.chapterName)}</td>
          <td class="status">${escapeHtml(row.chapterLifecycleStatus)}</td>
          <td>${escapeHtml(getChapterLocation(row))}</td>
          <td>${escapeHtml(row.presidentName)}</td>
          <td>${escapeHtml(row.secretaryName)}</td>
        </tr>`,
    )
    .join("");

  return `
    <html>
      <head>
        <meta charset="UTF-8" />
        <style>
          table { border-collapse: collapse; font-family: Arial, sans-serif; width: 100%; }
          .title { background: #17415f; color: #ffffff; font-size: 18px; font-weight: 700; text-align: center; }
          .meta { background: #f2f9ff; color: #334155; font-size: 12px; text-align: left; }
          th { background: #aedaff; color: #111827; font-weight: 700; text-align: center; }
          td, th { border: 1px solid #94a3b8; padding: 8px; vertical-align: middle; mso-number-format: "\\@"; }
          td { color: #111827; font-size: 12px; }
          .serial { text-align: center; width: 45px; }
          .status { text-align: center; font-weight: 700; }
        </style>
      </head>
      <body>
        <table>
          <colgroup>
            <col style="width: 45px" />
            <col style="width: 230px" />
            <col style="width: 150px" />
            <col style="width: 360px" />
            <col style="width: 190px" />
            <col style="width: 190px" />
          </colgroup>
          <tr><td class="title" colspan="6">Chapter List</td></tr>
          <tr><td class="meta" colspan="6">Generated: ${escapeHtml(generatedAt)} | Total Chapters: ${rows.length}</td></tr>
          <tr>
            <th>Sr. No.</th>
            <th>Chapter Name</th>
            <th>Chapter Status</th>
            <th>Location</th>
            <th>Chapter President</th>
            <th>Secretary Name</th>
          </tr>
          ${bodyRows || '<tr><td colspan="6" class="status">No chapters available</td></tr>'}
        </table>
      </body>
    </html>`;
};

const AdminChapterListPage = () => {
  const [allChapters, setAllChapters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [chapterStatusFilter, setChapterStatusFilter] = useState("All");
  const [lifecycleFilter, setLifecycleFilter] = useState("All");
  const [stateFilter, setStateFilter] = useState("All");
  const [districtFilter, setDistrictFilter] = useState("All");
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [meta, setMeta] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPrevPage: false,
  });

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    window.scrollTo({ top: 0, behaviour: "smooth" });
  }, []);

  const handleStatusSelectChange = (row, newStatus) => {
    if (!row || !newStatus || row.chapterLifecycleStatus === newStatus) return;
    setPendingStatusChange({
      chapterId: row._id,
      chapterName: row.chapterName,
      oldStatus: row.chapterLifecycleStatus,
      newStatus,
    });
    setStatusDialogOpen(true);
  };

  const handleConfirmStatusChange = async () => {
    if (!pendingStatusChange) return;
    const { chapterId, chapterName, newStatus } = pendingStatusChange;

    try {
      setUpdatingStatus(true);
      await ApiCallService.put(
        `${process.env.REACT_APP_BASE_URL}/chapter/${chapterId}/lifecycle-status`,
        { chapterLifecycleStatus: newStatus }
      );

      setAllChapters((prevChapters) =>
        prevChapters.map((ch) =>
          ch._id === chapterId
            ? { ...ch, chapterLifecycleStatus: newStatus }
            : ch
        )
      );

      message.success(
        `Status for "${chapterName}" updated to "${newStatus}" successfully.`
      );
      setStatusDialogOpen(false);
      setPendingStatusChange(null);
    } catch (error) {
      console.error("Error updating chapter status:", error);
      message.error(
        error?.response?.data?.message || "Failed to update chapter status."
      );
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleCancelStatusChange = () => {
    if (updatingStatus) return;
    setStatusDialogOpen(false);
    setPendingStatusChange(null);
  };

  useEffect(() => {
    const fetchChapters = async () => {
      try {
        setLoading(true);
        const response = await ApiCallService.get(
          `${process.env.REACT_APP_BASE_URL}/getAllChaptersData?page=1&limit=1000`,
        );

        const chapters = Array.isArray(response?.data) ? response.data : [];
        setAllChapters(chapters);
        setMeta(response?.meta || {});
      } catch (error) {
        console.error("Error fetching all chapters:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchChapters();
  }, []);

  const tableRows = useMemo(() => {
    return (allChapters || []).map((chapter) => ({
      _id: chapter?._id || "",
      chapterName: chapter?.chapterName || "-",
      chapterStatus: chapter?.chapterStatus || "-",
      chapterLifecycleStatus: chapter?.chapterLifecycleStatus || "-",
      stateName: chapter?.location?.state?.stateName || "-",
      districtName: chapter?.location?.district?.districtName || "-",
      regionName: chapter?.location?.region?.regionName || "-",
      talukaName:
        chapter?.location?.taluka?.talukaName ||
        chapter?.location?.taluka ||
        " ",
      villageName:
        chapter?.location?.village?.villageName ||
        chapter?.location?.village ||
        " ",
      presidentName: getPersonName(
        chapter?.presidentName ||
          chapter?.chapterPresidentName ||
          chapter?.chapterPresident ||
          chapter?.president,
      ),
      secretaryName: getPersonName(
        chapter?.secretaryName ||
          chapter?.secretoryName ||
          chapter?.chapterSecretaryName ||
          chapter?.chapterSecretoryName ||
          chapter?.chapterSecretary ||
          chapter?.chapterSecretory ||
          chapter?.secretary ||
          chapter?.secretory,
      ),
    }));
  }, [allChapters]);

  const filteredRows = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return tableRows.filter((row) => {
      const matchesSearch = !normalizedSearch
        ? true
        : [
            row.chapterName,
            row.chapterStatus,
            row.chapterLifecycleStatus,
            row.stateName,
            row.districtName,
            row.regionName,
            row.talukaName,
            row.villageName,
            row.presidentName,
            row.secretaryName,
          ].some((value) =>
            String(value).toLowerCase().includes(normalizedSearch),
          );

      const matchesStatus =
        chapterStatusFilter === "All" || row.chapterStatus === chapterStatusFilter;
      const matchesLifecycle =
        lifecycleFilter === "All" || row.chapterLifecycleStatus === lifecycleFilter;
      const matchesState = stateFilter === "All" || row.stateName === stateFilter;
      const matchesDistrict =
        districtFilter === "All" || row.districtName === districtFilter;

      return matchesSearch && matchesStatus && matchesLifecycle && matchesState && matchesDistrict;
    });
  }, [chapterStatusFilter, lifecycleFilter, stateFilter, districtFilter, searchTerm, tableRows]);

  const statusOptions = useMemo(() => {
    return [...new Set(tableRows.map((row) => row.chapterStatus))].filter(Boolean);
  }, [tableRows]);

  const lifecycleOptions = useMemo(() => {
    return [...new Set(tableRows.map((row) => row.chapterLifecycleStatus))].filter(Boolean);
  }, [tableRows]);

  const stateOptions = useMemo(() => {
    return [...new Set(tableRows.map((row) => row.stateName))].filter((value) => value && value !== "-");
  }, [tableRows]);

  const districtOptions = useMemo(() => {
    return [...new Set(tableRows.map((row) => row.districtName))].filter((value) => value && value !== "-");
  }, [tableRows]);

  const AspirationalChaptersCount = filteredRows.filter((i) => i.chapterLifecycleStatus === "Aspirational").length
  const ProspectiveChaptersCount = filteredRows.filter((i) => i.chapterLifecycleStatus === "Prospective").length
  const VibrantChaptersCount = filteredRows.filter((i) => i.chapterLifecycleStatus === "Vibrant").length


  const getLifecycleChipSx = (status) => {
    const normalizedStatus = String(status || "").trim().toLowerCase();
    const colorMap = {
      vibrant: { bg: "#69BBFF", color: "#ffffff" },
      prospective: { bg: "#FF7049", color: "#ffffff" },
      aspirational: { bg: "#8E7CC3", color: "#ffffff" },
      approved: { bg: "#2E7D32", color: "#ffffff" },
      pending: { bg: "#F9A825", color: "#1f1f1f" },
      rejected: { bg: "#D32F2F", color: "#ffffff" },
      inactive: { bg: "#9E9E9E", color: "#ffffff" },
    };
    const palette = colorMap[normalizedStatus] || { bg: "#E3F2FD", color: "#17415F" };

    return {
      backgroundColor: palette.bg,
      color: palette.color,
      fontWeight: 700,
      minWidth: 96,
      "& .MuiChip-label": {
        px: 1.25,
      },
    };
  };

  // const CountCard = ({ name, count, color }) => {
  //   return (
  //     <Box
  //       sx={{
  //         width: "100%",
  //         height: 96,
  //         borderRadius: "999px",
  //         bgcolor: "#F2F9FF",
  //         border: "1px solid #ddd",
  //         display: "flex",
  //         alignItems: "center",
  //         overflow: "hidden",
  //         pl: 3,
  //         boxShadow: "0 4px 10px rgba(0,0,0,0.08)",
  //       }}
  //     >
  //       {/* NAME */}
  //       <Typography
  //       variant="subtitle1"
  //         sx={{
  //           flex: 1,
  //           fontWeight: 600,
  //           fontSize: { xs: "0.8rem", sm: "1.1rem" },
  //           lineHeight: 1.2, 
  //           mb: 0,
  //           color: "#0b0b0b",
  //         }}
  //       >
  //         {name}
  //       </Typography>

  //       {/* COUNT PILL */}
  //       <Box
  //         sx={{
  //           width: "35%",
  //           height: "100%",
  //           borderRadius: "999px",
  //           bgcolor: color,

  //           display: "flex",
  //           justifyContent: "center",
  //           alignItems: "center",

  //           flexShrink: 0,

  //           // Shadow cast towards the left
  //           boxShadow: "-12px 0 24px rgba(0,0,0,0.25)",
  //         }}
  //       >
  //         <Typography
  //         variant="h2"
  //           sx={{
  //             // fontSize: { xs: "1.5rem", sm: "2.2rem" },
  //             fontWeight: 700,
  //             color: "#fdfdfd",
  //           }}
  //         >
  //           <CountUpAnimation
  //             from={0}
  //             to={count}
  //             duration={1}
  //           />
  //         </Typography>
  //       </Box>
  //     </Box>
  //   );
  // };

  const CountCard = ({ name, count, accent = "#AEDAFF" }) => {
    return (
      <Box
        sx={{
          p: {xs:2.6, md:3},
          borderRadius: 2,
          bgcolor: "#F2F9FF",
          backgroundImage: "none",
          opacity: 1,

          width: {xs:"100%", md:"90%", xl:"100%", xxl:"85%"},
          height: {xs:125, md:96, xl:150},
          minHeight: 84,

          border: `1px solid ${accent}`,

          textAlign: "center",
          display: "flex",
          flexDirection: {xs:"column", sm:"row", xl:"column"},
          justifyContent: "space-between",
          alignItems: "center",

          position: "relative",
          boxShadow: "0 4px 10px rgba(0,0,0,0.08)",

          transition: "transform 180ms ease, box-shadow 180ms ease",

          "&:hover": {
            transform: "translateY(-3px)",
            boxShadow: "0 8px 18px rgba(0,0,0,0.12)",
          },
        }}
      >
        <Typography
          variant="h5"
          sx={{
            textWrap: "wrap",
            mb: 0,
            mr: {sm:1.5, md:1},
            textAlign:{md:"left"},
            fontSize:{sm:"18px",md:"16px", xl:"20px", xxl:"24px"}
          }}
          color="#000000"
        >
          {name}
        </Typography>

        <Typography
          variant="h4"
          sx={{
            fontSize: { xs: "1.5rem", sm: "2.2rem" },
            lineHeight: 1.1,
            mt: 0,
          }}
          fontWeight={700}
          color="#0b0b0b"
        >
          <CountUpAnimation
            from={0}
            to={count}
            duration={1}
          />
        </Typography>
      </Box>
    );
  };

  const columns = [
    {
      field: "chapterName",
      headerName: "Chapter Name",
        disableSort: true,
      // disableSearch: true,
      renderCell: (row) => (
        <Typography sx={{ fontWeight: 600 }}>{row.chapterName}</Typography>
      ),
    },
    {
      field: "chapterLifecycleStatus",
      headerName: "Chapter Status",
      disableSort: true,
      disableSearch: true,
      renderCell: (row) => {
        const currentStatus = LIFECYCLE_STATUS_OPTIONS.includes(
          row.chapterLifecycleStatus
        )
          ? row.chapterLifecycleStatus
          : "Prospective";

        return (
          <Select
            size="small"
            value={currentStatus}
            onChange={(e) => handleStatusSelectChange(row, e.target.value)}
            renderValue={(selected) => (
              <Chip
                label={selected}
                size="small"
                sx={getLifecycleChipSx(selected)}
              />
            )}
            sx={{
              height: 32,
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
              "& .MuiSelect-select": {
                p: 0,
                pr: "22px !important",
                display: "flex",
                alignItems: "center",
              },
              "& .MuiSelect-icon": {
                right: 0,
                fontSize: "1.2rem",
                color: "#64748b",
              },
            }}
          >
            {LIFECYCLE_STATUS_OPTIONS.map((status) => (
              <MenuItem key={status} value={status} sx={{ py: 1 }}>
                <Chip
                  label={status}
                  size="small"
                  sx={getLifecycleChipSx(status)}
                />
              </MenuItem>
            ))}
          </Select>
        );
      },
    },
 {
  field: "stateName",
  headerName: "State",
    disableSort: true,
      disableSearch: true,
  renderCell: (row) => <Typography>{row.stateName || "-"}</Typography>,
},
{
  field: "regionName",
  headerName: "Region",
    disableSort: true,
      disableSearch: true,
  renderCell: (row) => <Typography>{row.regionName || "-"}</Typography>,
},
{
  field: "districtName",
  headerName: "District",
    disableSort: true,
      disableSearch: true,
  renderCell: (row) => <Typography>{row.districtName || "-"}</Typography>,
},

  ];

  return (
    <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
      <Stack spacing={2}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Full Chapter List
          </Typography>
        </Box>

        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          {/* <Select
            size="small"
            value={chapterStatusFilter}
            displayEmpty
            onChange={(event) => setChapterStatusFilter(event.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="All">All Chapter Status</MenuItem>
            {statusOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select> */}

          <Select
            size="small"
            value={lifecycleFilter}
            displayEmpty
            onChange={(event) => setLifecycleFilter(event.target.value)}
            sx={{ minWidth: {xs:"100%", md:220, } }}
          >
            <MenuItem value="All">All Status</MenuItem>
            {lifecycleOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>

          <Select
            size="small"
            value={stateFilter}
            displayEmpty
            onChange={(event) => {
              setStateFilter(event.target.value);
              setDistrictFilter("All");
            }}
            sx={{ minWidth: {xs:"100%", md:220, } }}
          >
            <MenuItem value="All">All States</MenuItem>
            {stateOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>

          <Select
            size="small"
            value={districtFilter}
            displayEmpty
            onChange={(event) => setDistrictFilter(event.target.value)}
            sx={{ minWidth: {xs:"100%", md:220, } }}
          >
            <MenuItem value="All">All Districts</MenuItem>
            {districtOptions
              .filter((option) => stateFilter === "All" || tableRows.find((row) => row.stateName === stateFilter && row.districtName === option))
              .map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
          </Select>
        </Stack>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems="center"
        >
          {/* 2x2 COUNT CARD GRID */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {xs:"repeat(1, 1fr)", sm:"repeat(2, 1fr)", xl:"repeat(4, 1fr)"},
              gap: 2,
              width: "100%",
            }}
          >
            <CountCard
              name="Total Chapters"
              count={
                VibrantChaptersCount +
                AspirationalChaptersCount +
                ProspectiveChaptersCount
              }
              color="#69D6A3"
            />

            <CountCard
              name="Vibrant Chapters"
              count={VibrantChaptersCount}
              color="#69BBFF"
            />

            <CountCard
              name="Aspirational Chapters"
              count={AspirationalChaptersCount}
              color="#8E7CC3"
            />

            <CountCard
              name="Prospective Chapters"
              count={ProspectiveChaptersCount}
              color="#FF7049"
            />
          </Box>

          {/* DOUGHNUT */}
          <Box
            sx={{
              width: "100%",
              maxWidth: 360,
              height: 270,
              mx: "auto",
              mt: 1.5,
            }}
          >
            <Doughnut
              data={{
                labels: ["Aspirational", "Prospective", "Vibrant"],
                datasets: [
                  {
                    data: [
                      AspirationalChaptersCount,
                      ProspectiveChaptersCount,
                      VibrantChaptersCount,
                    ],
                    backgroundColor: [
                      "#8E7CC3",
                      "#FF7049",
                      "#69BBFF",
                    ],
                    borderColor: [
                      "#8E7CC3",
                      "#FF7049",
                      "#69BBFF",
                    ],
                    borderWidth: 1,
                    hoverOffset: 10,
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: "bottom",
                  },
                },
              }}
            />
          </Box>
        </Stack>

         <Box
          sx={{ width: "100%", height: 2, bgcolor: "#C7C7C7", mb: 2, mt: 2 }}
        />

        <Box sx={{display:"flex", flexDirection: "row", gap:0.5, justifyContent:"space-between"}}>

          <TextField
            size="small"
            placeholder="Search by chapter, status, state, district or region"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: "text.secondary", mr: 1 }} />,
            }}
            sx={{ width:{xs: 250, sm:285, md:340} }}
          />

          <Button
            variant="contained"
            onClick={() => downloadChapterExcel(allChapters)}
            disabled={loading}
            size="small"
            sx={{
              color:"black",
              textTransform: "none",
              borderRadius: 1,
              bgcolor: "#AEDAFF",
              fontWeight: 700,
              gap:0.5,
              boxShadow: "none",
              "&:hover": { bgcolor: "#9dd0f5", color:"black", boxShadow: "none" },
            }}
          >
            <FileDownloadIcon />
            {isMobile ? "" : "Download Excel"}
          </Button>
        </Box>

        <ReactTable
          columns={columns}
          rows={filteredRows}
          loading={loading}
          rowKey="_id"
          initialRowsPerPage={10}
          headerColor="linear-gradient(to right, #004ecb, #00c6ff)"
          headerTextColor="#fff"
        />
      </Stack>

      {/* Confirmation Dialog for Status Change */}
      <Dialog
        open={statusDialogOpen}
        onClose={updatingStatus ? undefined : handleCancelStatusChange}
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1,
            minWidth: { xs: "90%", sm: 420 },
            boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 700, fontSize: "1.2rem", color: "#17415F" }}>
          Confirm Status Change
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          <DialogContentText sx={{ color: "#334155", fontSize: "0.95rem", mb: 2 }}>
            Are you sure you want to change the status for chapter{" "}
            <strong>{pendingStatusChange?.chapterName}</strong>?
          </DialogContentText>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              p: 2,
              bgcolor: "#F8FAFC",
              borderRadius: 2,
              border: "1px solid #E2E8F0",
            }}
          >
            <Chip
              label={pendingStatusChange?.oldStatus || "-"}
              size="small"
              sx={getLifecycleChipSx(pendingStatusChange?.oldStatus)}
            />
            <Typography sx={{ fontWeight: 700, color: "#64748B" }}>→</Typography>
            <Chip
              label={pendingStatusChange?.newStatus || "-"}
              size="small"
              sx={getLifecycleChipSx(pendingStatusChange?.newStatus)}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={handleCancelStatusChange}
            disabled={updatingStatus}
            variant="outlined"
            sx={{
              textTransform: "none",
              borderRadius: 2,
              borderColor: "#CBD5E1",
              color: "#475569",
              "&:hover": { borderColor: "#94A3B8", bgcolor: "#F1F5F9" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmStatusChange}
            disabled={updatingStatus}
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: 2,
              bgcolor: "#17415F",
              color: "#ffffff",
              fontWeight: 600,
              "&:hover": { bgcolor: "#102e43" },
            }}
          >
            {updatingStatus ? <CircularProgress size={20} color="inherit" /> : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default AdminChapterListPage;